<template>
  <div
    class="visit-list-item"
    :style="`min-height: ${cardMinHeight}`"
    @click="goDetail"
  >
    <div v-if="item.is_draft" class="draft-corner">
      <span class="draft-corner-text">{{ $t('hcp_visit_draft_tag') }}</span>
    </div>
    <div class="modify" :style="`background: ${color}`"></div>
    <div style="flex: 1; padding: 20px 0 15px">
      <div class="item-main">
        <div class="custom-item-left">
          <img src="./../../../assets/lundbeck/portrait.png" class="avatar" />
        </div>
        <div class="custom-item-right">
          <div class="header clearfix">
            <div class="name" style="flex: 1">
              <span class="name-w" :class="{ 'name-placeholder': !hasPhysicianName }">
                {{ physicianName | displayPhysicianName }}
              </span>
            </div>
          </div>
          <p class="department">
            {{ physicianDepartment }}
            {{ physicianTitle }}
          </p>
          <div class="address">
            <div class="hospital">
              <span class="e-ellipsis-l1">
                {{ physicianInstitution }}
              </span>
            </div>
          </div>
          <div class="address">
            <div class="hospital">
              <span v-if="displayCallTime" class="e-ellipsis-l1">
                {{ displayCallTime }}
              </span>
              <span v-if="item.insight_time" class="e-ellipsis-l1">
                {{ item.insight_time.substring(0, 10) }}
              </span>
            </div>
            <div v-if="showMetaTags" class="meta-tags">
              <div v-if="mealTagText" class="meta-tag filled">
                {{ mealTagText }}
              </div>
              <div v-if="feedbackTagText" class="meta-tag" :class="feedbackTagClass">
                {{ feedbackTagText }}
              </div>
            </div>
          </div>
          <div v-if="item.physician_mr_call__physician_mr_call_companions && item.physician_mr_call__physician_mr_call_companions.length > 0" class="address">
            <div class="hospital">
              <span class="e-ellipsis-l1" style="color: #333; font-size: 12px;">
                {{ $t('hcp_visit_companion_label') }}{{ item.physician_mr_call__physician_mr_call_companions.map(c => c.user_name).join(', ') }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div v-if="!isSub && displayAiSummary" class="ai-summary-card">
        <div class="ai-summary-text">{{ displayAiSummary }}</div>
        <div class="ai-summary-footer">
          <AISourceTag class="summary-source-tag" />
        </div>
      </div>
      <div
        v-if="isSub"
        class="item-sub"
        style="
          padding-right: 10px;
          height: 50px;
          overflow: hidden;
          text-overflow: ellipsis;
        "
      >
        <div style="margin-bottom: 10px; color: #8bc3ee">
          {{ item.insight_question?.question }}
        </div>
        <div class="max-line-2">
          {{ item.insight_content }}
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import AISourceTag from '@/components/AISourceTag.vue';
import { isEnableAI } from '@/utils/index';

export default {
  name: 'VisitListItem',
  components: {
    AISourceTag,
  },
  props: {
    color: {
      type: String,
      default: '',
    },
    isSub: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    physicianName() {
      return this.item?.physician?.physician_name || this.$t('hcp_visit_unselected_physician');
    },
    hasPhysicianName() {
      return Boolean(this.item?.physician?.physician_name);
    },
    physicianDepartment() {
      return this.item?.physician?.department_name || '';
    },
    physicianTitle() {
      return this.item?.physician?.professional_title || '';
    },
    physicianInstitution() {
      return this.item?.physician?.institution_name || '';
    },
    displayAiSummary() {
      return isEnableAI() ? this.item?.call_summary || '' : '';
    },
    cardMinHeight() {
      if (this.isSub) return '170px';
      return this.displayAiSummary ? '178px' : '105px';
    },
    displayCallTime() {
      const dateText = this.item?.call_time ? this.getVisitDateText(this.item.call_time) : '';
      const periodText = this.item?.call_period || '';
      return [dateText, periodText].filter(Boolean).join(' · ');
    },
    feedbackCount() {
      const count = Number(this.item?.feedback_count || 0);
      return Number.isNaN(count) ? 0 : count;
    },
    hasPendingFeedbackInput() {
      return Boolean(this.item?.has_pending_feedback_input);
    },
    hasFeedback() {
      return Boolean(this.item?.has_feedback || this.feedbackCount > 0);
    },
    mealTagText() {
      return this.item?.is_diner ? this.$t('hcp_visit_diner_tag') : '';
    },
    feedbackTagText() {
      if (this.hasFeedback) {
        return this.$t('hcp_visit_feedback_count', {count: this.feedbackCount});
      }
      return '';
    },
    showMetaTags() {
      return Boolean(this.mealTagText || this.feedbackTagText);
    },
    feedbackTagClass() {
      if (this.hasPendingFeedbackInput) {
        return 'pending';
      }
      if (this.hasFeedback) {
        return 'filled';
      }
      return 'empty';
    },
  },
  methods: {
    goDetail() {
      if (this.isSub) {
        this.$router.push(`insight-create?id=${this.item.physician_msl_insight_id}`);
        return;
      }

      const basePath = this.$route.path.startsWith('/gba') ? '/gba/visit-create' : '/hcp/visit-create';
      const params = [`isCoVisit=${this.item.is_co_call}`];

      if (this.item.physician_mr_call_id) {
        params.unshift(`id=${this.item.physician_mr_call_id}`);
      }

      if (this.item.draft_id) {
        params.push(`draftId=${this.item.draft_id}`);
      }

      this.$router.push(`${basePath}?${params.join('&')}`);
    },
    normalizeText(value) {
      if (value === null || value === undefined) return '';
      return String(value).trim();
    },
    getVisitDateText(callTime) {
      const rawDate = this.normalizeText(callTime).split('T')[0];
      if (!rawDate) return '';

      if (rawDate === this.getTodayDateString()) {
        return this.$t('today');
      }

      if (rawDate === this.getOffsetDateString(1)) {
        return this.$t('tomorrow');
      }

      return rawDate;
    },
    getTodayDateString() {
      return this.getOffsetDateString(0);
    },
    getOffsetDateString(offsetDays = 0) {
      const now = new Date();
      now.setDate(now.getDate() + offsetDays);
      const year = now.getFullYear();
      const month = `${now.getMonth() + 1}`.padStart(2, '0');
      const day = `${now.getDate()}`.padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
  },
};
</script>
<style scoped lang="scss">
@import '@/styles/var.scss';
.visit-list-item {
  position: relative;
  display: flex;
  align-items: stretch;
  background-color: #fff;
  border-radius: 20px;
  margin: 0 5px 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  .draft-corner {
    position: absolute;
    top: 0;
    right: 0;
    width: 68px;
    height: 68px;
    z-index: 2;
    pointer-events: none;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: #f0d48a;
      clip-path: polygon(100% 0, 0 0, 100% 100%);
      box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.45);
    }

    .draft-corner-text {
      position: absolute;
      top: 16px;
      right: 5px;
      display: block;
      max-width: 42px;
      color: #9b7535;
      font-size: 12px;
      font-weight: 400;
      line-height: 1.1;
      text-align: center;
      word-break: break-word;
      transform: rotate(45deg);
      transform-origin: center;
      text-transform: uppercase;
    }
  }
  .modify {
    width: 6px;
    opacity: 0.8;
  }
  .item-main {
    margin-left: 20px;
    display: flex;
    align-items: center;
    .custom-item-left {
      text-align: center;
      .avatar {
        width: 50px;
        height: 50px;
        border-radius: 50%;
      }
    }
    .custom-item-right {
      flex: 1;
      font-size: 12px;
      text-align: left;
      padding-left: 20px;
      @include suggest-detail-header();
      .count {
        font-size: 19px;
        color: #b2b2b2;
        .count-color {
          color: #00b852;
          min-width: 32px;
        }
      }
      .department {
        padding: 6px 0 4px 0;
      }
      .name-placeholder {
        color: #b7b7b7;
        font-weight: 400;
      }
      .address {
        color: #b2b2b2;
        display: flex;
        flex-direction: row;
        .hospital {
          flex: 1;
          min-width: 0;
          width: 0;
          line-height: 20px;
          span {
            display: block;
          }
        }
        .cube-btn {
          padding: 4px 8px;
          font-size: 11px;
          border-radius: 20px;
        }
        .meta-tags {
          display: flex;
          align-items: center;
          flex-shrink: 0;
          gap: 8px;
          margin-left: 8px;
          margin-right: 12px;
        }
        .meta-tag {
          flex-shrink: 0;
          align-self: center;
          padding: 0 8px;
          border-radius: 999px;
          font-size: 11px;
          line-height: 18px;
          border: 1px solid currentColor;
          color: #999;
          background: transparent;

          &.filled {
            color: #00b852;
          }

          &.pending {
            color: #b87007;
          }

          &.empty {
            color: #999;
          }
        }
      }
    }
  }
  .ai-summary-card {
    margin: 8px 12px 0 12px;
    padding: 9px 10px 8px;
    border-radius: 12px;
    background: linear-gradient(180deg, #fbfffd 0%, #f1fff7 100%);
    border: 1px solid rgba(0, 184, 82, 0.06);
  }
  .ai-summary-text {
    color: #7b6158;
    font-size: 12px;
    line-height: 18px;
  }
  .ai-summary-footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 4px;
    opacity: 0.82;
  }
  .ai-summary-footer ::v-deep .ai-source-tag {
    background: rgba(255, 255, 255, 0.34);
    color: rgba(123, 97, 88, 0.62);
    padding: 2px 6px;
    border-radius: 999px;
    backdrop-filter: none;
  }
  .ai-summary-footer ::v-deep .tag-icon-wrapper {
    margin-right: 3px;
  }
  .ai-summary-footer ::v-deep .tag-text {
    letter-spacing: 0.1px;
  }
  .item-sub {
    font-size: 12px;
    text-align: left;
    margin-left: 25px;
    margin-top: 10px;
  }
}
</style>
